import { useRecoilTransaction_UNSTABLE } from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { selectedInstallAreaIdState } from "~/containers/Proposal/state/designs/installAreas"

import type { TransactionOperation } from "."
import { deleteArea } from "./deleteArea"
import { deletePanels } from "./deletePanels"
import { edgeIndex, edgeSelectionState } from "../edges"
import { nodeIndex } from "../nodes"
import {
  getNodeSelectionState,
  nodeSelectionState_INTERNAL,
} from "../nodeSelection"
import { panelSelectionState } from "../panels"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

export const deleteMeshSelection: TransactionOperation = (transactors) => {
  const { get, set, reset } = transactors
  const nodeSelection = getNodeSelectionState(transactors)
  const edgeSelection = get(edgeSelectionState)
  const changes: UnknownChangeForUnknownElement[] = []
  set(nodeIndex, (ids) =>
    ids.filter((id) => {
      if (!nodeSelection.has(id)) {
        return true
      }
      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `DELETE`,
            family: `NODE`,
            id,
          },
        }),
      )
    }),
  )
  const selectedNodeIds = Array.from(nodeSelection)
  // delete edges
  const otherNodeIds: string[] = []
  set(edgeIndex, (edgeIds) =>
    edgeIds.filter((edgeId) => {
      const shouldKeep =
        !edgeSelection.has(edgeId) &&
        !selectedNodeIds.some((nodeId) => edgeId.includes(nodeId))
      if (shouldKeep) {
        return true
      }

      // push other node to otherNodeIds
      const [nodeId1, nodeId2] = edgeId.split(`/`)
      const getOtherNode = (currentNodeId: string) => {
        if (
          !selectedNodeIds.includes(currentNodeId) &&
          !otherNodeIds.includes(currentNodeId)
        ) {
          otherNodeIds.push(currentNodeId)
        }
      }
      getOtherNode(nodeId1)
      getOtherNode(nodeId2)

      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `DELETE`,
            family: `EDGE`,
            id: edgeId,
          },
        }),
      )
    }),
  )

  // delete nodes that are no longer connected to anything
  const edgeIds = get(edgeIndex)
  set(nodeIndex, (ids) =>
    ids.filter((id) => {
      if (
        edgeIds.some((edgeId) => edgeId.includes(id)) ||
        !otherNodeIds.includes(id)
      ) {
        return true
      }
      changes.push(
        generateElementChange(transactors, {
          action: {
            type: `DELETE`,
            family: `NODE`,
            id,
          },
        }),
      )
    }),
  )

  if (changes.length) divergeTimeline(transactors, changes)
  reset(nodeSelectionState_INTERNAL)
  reset(edgeSelectionState)
}

export const deletePanelSelection: TransactionOperation = (transactors) => {
  const { get } = transactors
  const panelSelection = get(panelSelectionState)
  const areaSelection = get(selectedInstallAreaIdState)
  deletePanels(transactors, panelSelection)
  if (areaSelection && panelSelection.size === 0) {
    deleteArea(transactors, areaSelection)
  }
}

export const deleteSelection: TransactionOperation = (transactors) => {
  const { get } = transactors
  const { phase } = get(designLocalState)
  switch (phase) {
    case `Outline`:
      deleteMeshSelection(transactors)
      break
    case `Panel Placement`:
      deletePanelSelection(transactors)
      break
  }
}

export const useDeleteSelection = (
  ...deps: ReadonlyArray<unknown>
): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => deleteSelection(transactors),
    deps,
  )
